import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recipesApi } from '@/api/recipes';
import { useToast }   from '@/providers/ToastProvider';
import { useAuth }    from '@/providers/AuthProvider';
import {
  PageHeader, Button, FormField, Input, Card, CardContent, Spinner,
} from '@/components/ui';
import { Save } from 'lucide-react';

interface SettingField {
  key:          string;
  label:        string;
  description:  string;
  type:         'text' | 'number' | 'select';
  options?:     { value: string; label: string }[];
}

const SETTING_FIELDS: SettingField[] = [
  { key: 'app_name',         type: 'text',   label: 'Application Name',         description: 'Display name shown in the browser tab and header' },
  { key: 'default_language', type: 'select', label: 'Default Language',          description: 'Fallback UI language for new users',
    options: [{ value: 'en', label: 'English' }, { value: 'es', label: 'Español' }] },
  { key: 'default_tax_rate', type: 'number', label: 'Default Tax Rate (0–1)',    description: 'Pre-filled on new recipes, e.g. 0.10 for 10%' },
  { key: 'default_margin',   type: 'number', label: 'Default Desired Margin (0–1)', description: 'Pre-filled on new recipes, e.g. 0.35 for 35%' },
  { key: 'currency_symbol',  type: 'text',   label: 'Currency Symbol',           description: 'Symbol shown in cost displays, e.g. $ or €' },
  { key: 'currency_code',    type: 'text',   label: 'Currency Code (ISO 4217)',  description: 'e.g. USD, EUR, MXN' },
  { key: 'kitchen_refresh_s', type: 'number', label: 'Kitchen Refresh (seconds)', description: 'How often the kitchen display auto-refreshes' },
  { key: 'work_hour_cost',    type: 'number', label: 'Work Hour Cost',            description: 'Labour cost per hour used to compute recipe cost from prep time' },
];

export default function SettingsPage() {
  const { t }         = useTranslation();
  const queryClient   = useQueryClient();
  const { toast } = useToast();
  const { isAdmin }   = useAuth();

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn:  recipesApi.listSettings,
  });

  // Local draft state, keyed by setting key
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const getValue = (key: string) =>
    key in drafts ? drafts[key] : (settings.find(s => s.key === key)?.value ?? '');

  const isDirty = (key: string) =>
    key in drafts && drafts[key] !== settings.find(s => s.key === key)?.value;

  const saveMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      recipesApi.updateSetting(key, value),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });
      toast(t('settings.saved'), 'success');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const save = (key: string) => {
    if (!isDirty(key)) return;
    saveMutation.mutate({ key, value: drafts[key] });
  };

  if (!isAdmin) {
    return (
      <div className="py-20 text-center text-muted-foreground">{t('common.adminOnly')}</div>
    );
  }

  return (
    <div>
      <PageHeader title={t('nav.settings')} subtitle={t('settings.subtitle')} />

      {isLoading ? (
        <Spinner className="py-20" />
      ) : (
        <div className="max-w-2xl space-y-4">
          {SETTING_FIELDS.map(field => (
            <Card key={field.key}>
              <CardContent className="pt-4">
                <div className="flex items-end gap-3">
                  <div className="flex-1 min-w-0">
                    <FormField label={field.label}>
                      {field.type === 'select' ? (
                        <select
                          className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          value={getValue(field.key)}
                          onChange={e => setDrafts(prev => ({ ...prev, [field.key]: e.target.value }))}
                          disabled={!isAdmin}
                        >
                          {field.options?.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          type={field.type}
                          step={field.type === 'number' ? '0.01' : undefined}
                          value={getValue(field.key)}
                          onChange={e => setDrafts(prev => ({ ...prev, [field.key]: e.target.value }))}
                          disabled={!isAdmin}
                        />
                      )}
                    </FormField>
                    <p className="mt-1 text-xs text-muted-foreground">{field.description}</p>
                  </div>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant={isDirty(field.key) ? 'default' : 'outline'}
                      disabled={!isDirty(field.key) || saveMutation.isPending}
                      onClick={() => save(field.key)}
                      className="shrink-0"
                    >
                      <Save size={14} /> {t('common.save')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
